import { useMemo, useState } from 'react';
import type { Stage, UseChecklistResult } from '../types';
import { useLanguage } from '../i18n/context';
import { getUi } from '../i18n/ui';
import styles from '../App.module.css';

interface SearchBoxProps {
  stages: Stage[];
  checklist: UseChecklistResult;
  onSelectStage: (stageId: string) => void;
}

interface Hit {
  stageId: string;
  stageTitle: string;
  itemId: string;
  text: string;
  category: string;
  checked: boolean;
}

export function SearchBox({ stages, checklist, onSelectStage }: SearchBoxProps) {
  const { locale } = useLanguage();
  const ui = getUi(locale);
  const [query, setQuery] = useState('');

  const hits = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (q.length < 1) return [] as Hit[];
    const results: Hit[] = [];
    for (const stage of stages) {
      for (const item of stage.checklist) {
        const hay = `${item.text} ${item.note ?? ''}`.toLowerCase();
        if (!hay.includes(q)) continue;
        results.push({
          stageId: stage.id,
          stageTitle: stage.title,
          itemId: item.id,
          text: item.text,
          category: ui.checklist.categoryLabels[item.category],
          checked: checklist.isChecked(stage.id, item.id),
        });
        if (results.length >= 12) return results;
      }
    }
    return results;
  }, [query, stages, checklist, ui.checklist.categoryLabels]);

  return (
    <section className={styles.searchBox} aria-label={ui.search.ariaLabel}>
      <label className={styles.searchLabel}>
        <span className={styles.srOnly}>{ui.search.placeholder}</span>
        <input
          type="search"
          className={styles.searchInput}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={ui.search.placeholder}
          autoComplete="off"
        />
      </label>
      {query.trim() && (
        <ul className={styles.searchResults}>
          {hits.length === 0 ? (
            <li className={styles.searchEmpty}>{ui.search.empty}</li>
          ) : (
            hits.map((hit) => (
              <li key={`${hit.stageId}:${hit.itemId}`} className={styles.searchHit}>
                <button
                  type="button"
                  className={styles.searchHitButton}
                  onClick={() => {
                    onSelectStage(hit.stageId);
                    setQuery('');
                  }}
                >
                  <span className={styles.searchHitText}>{hit.text}</span>
                  <span className={styles.searchHitMeta}>
                    {hit.stageTitle} · {hit.category}
                    {hit.checked ? ` · ${ui.search.done}` : ''}
                  </span>
                </button>
              </li>
            ))
          )}
        </ul>
      )}
    </section>
  );
}
